/**
 * Client Portal V2 - Shared Layout
 * =================================
 * Consistent layout with navigation, header, and branding.
 */

import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { apiClient, clientAuth } from "@/lib/clientApi";
import {
  Building2, LayoutDashboard, Briefcase, FileText, Receipt,
  FolderOpen, User, LogOut, Bell, ChevronDown, Menu, X,
  AlertTriangle, CheckCircle, Clock
} from "lucide-react";
import { Badge } from "@/components/ui/badge";

const navItems = [
  { path: "/client-dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/client-v2/jobs", label: "Jobs", icon: Briefcase },
  { path: "/client-v2/quotes", label: "Quotes", icon: FileText },
  { path: "/client-v2/invoices", label: "Invoices", icon: Receipt },
  { path: "/client-v2/documents", label: "Documents", icon: FolderOpen },
];

export default function ClientLayout({ children }) {
  const [clientInfo, setClientInfo] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Check auth
    if (!clientAuth.isAuthenticated()) {
      navigate("/client-login");
      return;
    }

    // Load client info
    const stored = localStorage.getItem("client_info");
    if (stored) {
      try {
        setClientInfo(JSON.parse(stored));
      } catch (e) {}
    }

    // Fetch alerts
    fetchAlerts();
  }, [navigate]);

  const fetchAlerts = async () => {
    try {
      const res = await apiClient.get("/api/client/alerts");
      if (res.data?.alerts) {
        setAlerts(res.data.alerts);
      }
    } catch (err) {
      console.error("Failed to fetch alerts:", err);
    }
  };

  const handleLogout = () => {
    clientAuth.logout();
  };

  const brandName = clientInfo?.brand_name || clientInfo?.name || "Client Portal";
  const brandTheme = clientInfo?.brand_theme || "teal";

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + "/");

  const criticalAlerts = alerts.filter(a => a.severity === "critical").length;
  const highAlerts = alerts.filter(a => a.severity === "high").length;
  const totalAlerts = alerts.length;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <button
                className="lg:hidden p-2 rounded-lg hover:bg-slate-100"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
              <Link to="/client-dashboard" className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <div className="hidden sm:block">
                  <h1 className="font-bold text-slate-900">{brandName}</h1>
                  <p className="text-xs text-slate-500">Client Portal</p>
                </div>
              </Link>
            </div>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive(item.path)
                      ? "bg-teal-50 text-teal-700"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </Link>
              ))}
            </nav>

            {/* Right side */}
            <div className="flex items-center gap-2">
              {/* Alerts bell */}
              <div className="relative">
                <button
                  className="relative p-2 rounded-lg hover:bg-slate-100"
                  onClick={() => setAlertsOpen(!alertsOpen)}
                  data-testid="alerts-bell"
                >
                  <Bell className="w-5 h-5 text-slate-600" />
                  {totalAlerts > 0 && (
                    <span className={`absolute -top-1 -right-1 w-5 h-5 rounded-full text-xs text-white flex items-center justify-center ${
                      criticalAlerts > 0 ? "bg-red-500" : highAlerts > 0 ? "bg-orange-500" : "bg-teal-500"
                    }`}>
                      {totalAlerts}
                    </span>
                  )}
                </button>

                {/* Alerts dropdown */}
                {alertsOpen && (
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50">
                    <div className="px-4 py-2 border-b border-slate-100">
                      <h3 className="font-semibold text-slate-900">Alerts</h3>
                    </div>
                    {alerts.length > 0 ? (
                      <div className="max-h-80 overflow-y-auto">
                        {alerts.map((alert, idx) => (
                          <Link
                            key={idx}
                            to={alert.link || "#"}
                            className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50"
                            onClick={() => setAlertsOpen(false)}
                          >
                            {alert.severity === "critical" && <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />}
                            {alert.severity === "high" && <Clock className="w-5 h-5 text-orange-500 shrink-0 mt-0.5" />}
                            {alert.severity === "medium" && <Bell className="w-5 h-5 text-yellow-500 shrink-0 mt-0.5" />}
                            <div>
                              <p className="text-sm text-slate-700">{alert.title}</p>
                            </div>
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <div className="px-4 py-6 text-center text-slate-500 text-sm">
                        <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
                        No alerts
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* User menu */}
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-100 text-sm text-slate-600"
                data-testid="client-logout"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Nav */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-b border-slate-200 px-4 py-2">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium ${
                isActive(item.path)
                  ? "bg-teal-50 text-teal-700"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
              onClick={() => setMobileMenuOpen(false)}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </Link>
          ))}
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {children}
      </main>
    </div>
  );
}
